/* eslint-disable max-lines-per-function */
import { CheckCircle2, Pencil, Users, XCircle } from "lucide-react";
import { useState, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { Checkbox } from "../../../../shared/components/ui/checkbox";
import { TableCell, TableRow } from "../../../../shared/components/ui/table";
import type { BulkEditTitle, CastMember, CrewMember } from "../types";
import { EditableCell } from "./EditableCell";
import { EditCastModal } from "./EditCastModal";
import { EditCrewModal } from "./EditCrewModal";
import { TitleActionsDropdown } from "./TitleActionsDropdown";

interface CastCrewRowProps {
  readonly title: BulkEditTitle;
  readonly isSelected: boolean;
  readonly onSelect: (titleId: string, selected: boolean) => void;
  readonly onFieldUpdate: (
    titleId: string,
    field: string,
    value: string | number | boolean | string[] | CastMember[] | CrewMember[] | null,
  ) => Promise<void>;
  readonly onDelete: (titleId: string, titleName: string) => void;
  readonly onDuplicate: (titleId: string) => void;
}

/**
 * Single row in the Cast & Crew view table
 * Shows: Title, Director, Cast summary, Crew summary, Status
 */
export function CastCrewRow({
  title,
  isSelected,
  onSelect,
  onFieldUpdate,
  onDelete,
  onDuplicate,
}: Readonly<CastCrewRowProps>): ReactElement {
  const [showCastModal, setShowCastModal] = useState(false);
  const [showCrewModal, setShowCrewModal] = useState(false);
  const castCount = title.castData?.length || 0;
  const crewCount = title.crewData?.length || 0;

  const handleSaveCast = async (castData: CastMember[]) => {
    await onFieldUpdate(title.id, "castData", castData);
  };

  const handleSaveCrew = async (crewData: CrewMember[]) => {
    await onFieldUpdate(title.id, "crewData", crewData);
  };

  // Format cast names (show first 2, then +X more)
  const castDisplay = (() => {
    if (!title.castData || title.castData.length === 0) {
      return <span className="text-xs text-neutral-600 italic">No cast</span>;
    }
    const names = title.castData.slice(0, 2).map((c) => c.name);
    const remaining = castCount - 2;
    return (
      <span className="text-xs text-neutral-300 truncate">
        {names.join(", ")}
        {remaining > 0 && <span className="text-neutral-500"> +{remaining}</span>}
      </span>
    );
  })();

  // Format crew roles (show first 2 unique jobs, then +X more)
  const crewDisplay = (() => {
    if (!title.crewData || title.crewData.length === 0) {
      return <span className="text-xs text-neutral-600 italic">No crew</span>;
    }
    const uniqueJobs = [...new Set(title.crewData.map((c) => c.job))];
    const jobsToShow = uniqueJobs.slice(0, 2);
    const remaining = uniqueJobs.length - 2;
    return (
      <span className="text-xs text-neutral-300 truncate">
        {jobsToShow.join(", ")}
        {remaining > 0 && <span className="text-neutral-500 truncate"> +{remaining} more</span>}
      </span>
    );
  })();

  // Determine status (complete if has director, cast, and crew)
  const hasDirector = !!title.directorName;
  const hasCast = castCount > 0;
  const hasCrew = crewCount > 0;
  const isComplete = hasDirector && hasCast && hasCrew;
  const isPartial = hasDirector || hasCast || hasCrew;

  return (
    <TableRow className="hover:bg-neutral-900/30">
      <TableCell className="w-12">
        <Checkbox checked={isSelected} onCheckedChange={(checked) => onSelect(title.id, checked === true)} />
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-white truncate">{title.name}</span>
        </div>
      </TableCell>
      <TableCell>
        <EditableCell
          value={title.directorName ?? ""}
          onSave={(v) => onFieldUpdate(title.id, "directorName", v || null)}
          type="text"
          className="text-xs truncate"
          inputClassName="text-xs"
        />
      </TableCell>
      <TableCell>
        <button
          type="button"
          onClick={() => setShowCastModal(true)}
          className="flex flex-col gap-1 text-left hover:opacity-80 transition-opacity group w-full"
        >
          <div className="flex items-center gap-2">
            {castDisplay}
            <Pencil className="h-3 w-3 text-neutral-600 group-hover:text-lumi-accent transition-colors" />
          </div>
        </button>
      </TableCell>
      <TableCell>
        <button
          type="button"
          onClick={() => setShowCrewModal(true)}
          className="flex flex-col gap-1 text-left hover:opacity-80 transition-opacity group w-full"
        >
          <div className="flex items-center gap-2">
            {crewDisplay}
            <Pencil className="h-3 w-3 text-neutral-600 group-hover:text-lumi-accent transition-colors" />
          </div>
        </button>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          {isComplete && <CheckCircle2 className="h-4 w-4 text-green-400" />}
          {isPartial && !isComplete && <Users className="h-4 w-4 text-yellow-400" />}
          {!isPartial && <XCircle className="h-4 w-4 text-red-400" />}
          <span className="text-xs text-neutral-400">
            {isComplete && "Complete"}
            {isPartial && !isComplete && "Partial"}
            {!isPartial && "Missing"}
          </span>
        </div>
      </TableCell>
      <TableCell className="text-center">
        <TitleActionsDropdown titleId={title.id} titleName={title.name} onDelete={onDelete} onDuplicate={onDuplicate} />
      </TableCell>

      {/* Edit Cast Modal */}
      {createPortal(
        <EditCastModal
          open={showCastModal}
          onClose={() => setShowCastModal(false)}
          titleName={title.name}
          castData={title.castData}
          onSave={handleSaveCast}
        />,
        document.body,
      )}

      {/* Edit Crew Modal */}
      {createPortal(
        <EditCrewModal
          open={showCrewModal}
          onClose={() => setShowCrewModal(false)}
          titleName={title.name}
          crewData={title.crewData}
          onSave={handleSaveCrew}
        />,
        document.body,
      )}
    </TableRow>
  );
}
